/**
 * AdminInbox — every operator-to-support message thread + AI reply, plus a
 * personal-reply form that emails the operator via Resend. Grouped by
 * business_id so a single restaurant's whole conversation reads top-to-bottom.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Envelope, User } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function AdminInbox() {
  const [msgs, setMsgs] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/admin/inbox")
      .then((r) => setMsgs(r.data.messages))
      .catch((e) => { toast.error(e.response?.status === 403 ? "Admin access only" : "Couldn't load inbox"); setMsgs([]); });
    // Clear the sidebar red dot the moment the founder opens this page.
    api.post("/admin/inbox/mark_read").catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Group by business_id, newest-first per group.
  const groups = useMemo(() => {
    const by = new Map();
    for (const m of msgs || []) {
      if (!by.has(m.business_id)) by.set(m.business_id, { business_id: m.business_id, business_name: m.business_name, owner_email: m.owner_email, msgs: [] });
      by.get(m.business_id).msgs.push(m);
    }
    // Each group's most-recent-message timestamp (msgs are already newest-first).
    return Array.from(by.values()).sort((a, b) => {
      const ta = a.msgs[0]?.ts || ""; const tb = b.msgs[0]?.ts || "";
      return tb.localeCompare(ta);
    });
  }, [msgs]);

  const selected = selectedBiz ? groups.find((g) => g.business_id === selectedBiz) : null;

  const sendReply = async () => {
    if (!selected || !replyBody.trim()) return;
    setSending(true);
    try {
      await api.post("/admin/inbox/reply", {
        business_id: selected.business_id,
        subject: replySubject || `[BuzzerAI] Following up on your message`,
        body: replyBody,
      });
      toast.success(`Email sent to ${selected.owner_email}`);
      setReplyBody(""); setReplySubject("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="admin-inbox">
      <header className="sticky top-0 z-10 bg-[#1E3A2F] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><BuzzLogo size={22} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Super-Admin</div>
            <div className="font-heading font-extrabold">Support inbox</div>
          </div>
        </div>
        <div className="flex gap-3 text-sm">
          <Link to="/admin/customers" className="text-white/80 hover:text-white" data-testid="admin-nav-customers">Customers</Link>
          <Link to="/dashboard" className="text-white/80 hover:text-white">← Dashboard</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <aside className="paper-card overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E1DFD5] bg-[#E6E2D6] overline">Threads ({groups.length})</div>
          {msgs === null && <div className="p-4 text-xs text-[#5C6B61]">Loading…</div>}
          {msgs && groups.length === 0 && <div className="p-4 text-xs text-[#5C6B61]">No support messages yet.</div>}
          {groups.map((g) => (
            <button
              key={g.business_id}
              onClick={() => { setSelectedBiz(g.business_id); }}
              className={`w-full text-left px-4 py-3 border-b border-[#E1DFD5] hover:bg-[#F7F6F2] ${selectedBiz === g.business_id ? "bg-[#F7F6F2]" : ""}`}
              data-testid={`admin-inbox-thread-${g.business_id}`}
            >
              <div className="font-heading font-extrabold text-sm text-[#121914] truncate">{g.business_name || "Unknown"}</div>
              <div className="text-[11px] text-[#5C6B61] truncate">{g.owner_email}</div>
              <div className="text-[11px] text-[#5C6B61] mt-1 flex items-center gap-1">
                <span className="tag !py-0 !px-1.5 text-[9px]">{g.msgs.length}</span>
                <span>· {timeAgo(g.msgs[0]?.ts)}</span>
              </div>
            </button>
          ))}
        </aside>

        <main className="paper-card p-0 overflow-hidden">
          {!selected && (
            <div className="p-12 text-center text-sm text-[#5C6B61]">
              Pick a thread on the left to read the conversation.
            </div>
          )}
          {selected && (
            <>
              <div className="px-5 py-4 border-b border-[#E1DFD5]">
                <div className="font-heading font-extrabold text-[#121914]">{selected.business_name}</div>
                <div className="text-xs text-[#5C6B61]">{selected.owner_email}</div>
              </div>

              <div className="p-5 space-y-3 max-h-[500px] overflow-y-auto" data-testid="admin-inbox-transcript">
                {[...selected.msgs].reverse().map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      m.role === "user" ? "bg-[#1E3A2F] text-white" :
                      m.role === "admin_reply" ? "bg-[#F5C543]/30 border border-[#F5C543] text-[#121914]" :
                      "bg-[#F7F6F2] border border-[#E1DFD5] text-[#121914]"
                    }`}>
                      <div className="text-[9px] uppercase tracking-wider mb-1 opacity-70 flex items-center gap-1">
                        {m.role === "user" && <User size={9} />}
                        {m.role === "user" ? "Operator" : m.role === "admin_reply" ? "You (email)" : "AI"} · {timeAgo(m.ts)}
                      </div>
                      <div className="whitespace-pre-wrap leading-relaxed">{m.message}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#E1DFD5] p-4 bg-[#F7F6F2] space-y-2" data-testid="admin-inbox-reply-form">
                <div className="overline flex items-center gap-2"><Envelope size={12} /> Email {selected.owner_email}</div>
                <input
                  className="input-field !py-2 text-sm"
                  placeholder={`Subject — e.g. "Re: your BuzzerAI question"`}
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  data-testid="admin-reply-subject"
                />
                <textarea
                  className="input-field text-sm min-h-[120px]"
                  placeholder="Type your reply. Plain text is fine — we wrap it in a clean template."
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  data-testid="admin-reply-body"
                />
                <div className="flex justify-end">
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyBody.trim()}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="admin-reply-send"
                  >
                    <PaperPlaneTilt size={14} weight="fill" /> {sending ? "Sending…" : "Send reply"}
                  </button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
